window.ThemeCart = window.ThemeCart || {};

class ThemeCartFreeGiftController {
  constructor({
    owner = null,
    options = null,
    storageKey = "themeFreeGiftRemovedNotice",
    removedNoticeFallback = "",
    getMainCartOptions = null,
    getPrimaryDrawer = null,
    isAnyGateActive = null,
  } = {}) {
    this.owner = owner;
    this._options = options || {};
    this.storageKey = storageKey;
    this.removedNoticeFallback = removedNoticeFallback;
    this.getMainCartOptions = getMainCartOptions || (() => null);
    this.getPrimaryDrawer = getPrimaryDrawer || (() => document.querySelector("cart-drawer"));
    this.isAnyGateActive = isAnyGateActive || (() => false);

    this.syncInFlight = false;
    this.syncQueued = false;
    this.replaceContentPending = false;
    this.abortController = null;
    this.showRemovedNotice = false;
  }

  get options() {
    if (!this.owner) return this._options;
    if (!this.owner.options) this.owner.options = {};
    return this.owner.options;
  }

  set options(options) {
    if (this.owner) {
      this.owner.options = options || {};
    } else {
      this._options = options || {};
    }
  }

  get element() {
    return this.owner?.element || this.owner || this.getPrimaryDrawer() || document.body;
  }

  updateOptions(options) {
    if (!options) return;
    this.options = { ...(this.options || {}), ...options };
  }

  _setSyncInFlight(value) {
    this.syncInFlight = value === true;
    if (this.owner) this.owner._freeGiftSyncInFlight = this.syncInFlight;
  }

  _setSyncQueued(value) {
    this.syncQueued = value === true;
    if (this.owner) this.owner._freeGiftSyncQueued = this.syncQueued;
  }

  setReplaceContentPending(value) {
    this.replaceContentPending = value === true;
    if (this.owner) this.owner._freeGiftReplaceContentPending = this.replaceContentPending;
  }

  _setAbortController(controller) {
    this.abortController = controller || null;
    if (this.owner) this.owner._freeGiftAbortController = this.abortController;
  }

  _setShowRemovedNotice(value) {
    this.showRemovedNotice = value === true;
    if (this.owner) this.owner._showFreeGiftRemovedNotice = this.showRemovedNotice;
  }

  isCheckoutInProgress() {
    return (
      this.owner?._isCheckoutInProgress?.() === true ||
      window.__checkoutInProgress === true
    );
  }

  parseCurrencyLimitToCents(limitString, isoCode) {
    return window.ThemeCart.FreeGiftRules.parseCurrencyLimitToCents(limitString, isoCode);
  }

  _cartLineTotalCents(item) {
    return window.ThemeCart.CartLineRules.lineTotalCents(item);
  }

  _cartLinePropertyRaw(item, propertyKey) {
    return window.ThemeCart.CartLineRules.propertyRaw(item, propertyKey);
  }

  _themeFreeGiftPropertyMatch(item, propertyKey, propertyValue) {
    return window.ThemeCart.FreeGiftRules.propertyMatch(item, propertyKey, propertyValue);
  }

  _themeFreeGiftLineIds(item, variantIdStr) {
    return window.ThemeCart.FreeGiftRules.lineIds(item, variantIdStr, this.options);
  }

  _lineQualifiesAsThemeFreeGiftSlot(item, variantIdStr, propertyKey, propertyValue) {
    return window.ThemeCart.FreeGiftRules.lineQualifiesAsThemeFreeGiftSlot(
      item,
      this.options,
      variantIdStr,
      propertyKey,
      propertyValue
    );
  }

  _lineExcludedFromPaidMerch(item, variantIdStr, propertyKey, propertyValue) {
    return window.ThemeCart.FreeGiftRules.lineExcludedFromPaidMerch(
      item,
      this.options,
      variantIdStr,
      propertyKey,
      propertyValue
    );
  }

  async _postCartChange(changeUrl, headers, body) {
    const res = await window.ThemeCart.CartApi.change(body);
    return res.ok;
  }

  /**
   * Remove detected free-gift lines; retry with variant id(s) if key/line change fails.
   * @param {number[]} lineVariantIds variant_id from each matched line.
   */
  async _removeThemeFreeGiftLines(
    changeUrl,
    headers,
    freeGiftLineKeys,
    freeGiftLineIndexes,
    variantIdStr,
    lineVariantIds = []
  ) {
    const vidConfigured = Number(variantIdStr);
    const canTryConfigured = Number.isFinite(vidConfigured);
    const variantIdsUnique = new Set();
    let removedBySpecificLine = false;

    for (let i = 0; i < lineVariantIds.length; i++) {
      const n = Number(lineVariantIds[i]);
      if (Number.isFinite(n)) variantIdsUnique.add(n);
    }

    for (const lineKey of freeGiftLineKeys) {
      let ok = await this._postCartChange(changeUrl, headers, { id: lineKey, quantity: 0 });
      if (!ok && canTryConfigured) {
        ok = await this._postCartChange(changeUrl, headers, { id: vidConfigured, quantity: 0 });
      }
      if (ok) removedBySpecificLine = true;
    }

    if (!removedBySpecificLine) {
      const sortedLines = [...freeGiftLineIndexes].sort((a, b) => b - a);
      for (const line of sortedLines) {
        let ok = await this._postCartChange(changeUrl, headers, { line, quantity: 0 });
        if (!ok && canTryConfigured) {
          ok = await this._postCartChange(changeUrl, headers, { id: vidConfigured, quantity: 0 });
        }
        if (ok) removedBySpecificLine = true;
      }
    }

    if (!removedBySpecificLine) {
      for (const vid of variantIdsUnique) {
        await this._postCartChange(changeUrl, headers, { id: vid, quantity: 0 });
      }
    }
  }

  /**
   * Re-fetch cart and remove theme free-gift lines until none remain.
   * @returns {Promise<boolean>} true only when at least one gift line actually disappeared.
   */
  async _sweepRemoveThemeFreeGiftLines(changeUrl, headers, variantIdStr, propertyKey, propertyValue) {
    let sawGiftLines = false;
    for (let pass = 0; pass < 5; pass++) {
      const cartData = await window.ThemeCart.CartApi.fetchCart();
      const items = Array.isArray(cartData?.items) ? cartData.items : [];
      const keys = [];
      const indexes = [];
      const lineVariantIds = [];
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!this._lineQualifiesAsThemeFreeGiftSlot(item, variantIdStr, propertyKey, propertyValue))
          continue;
        if (item.key) keys.push(String(item.key));
        else indexes.push(i + 1);
        if (item.variant_id != null && item.variant_id !== "") {
          lineVariantIds.push(Number(item.variant_id));
        }
      }
      if (keys.length === 0 && indexes.length === 0) {
        return sawGiftLines;
      }
      sawGiftLines = true;
      await this._removeThemeFreeGiftLines(
        changeUrl,
        headers,
        keys,
        indexes,
        variantIdStr,
        lineVariantIds
      );
    }
    return false;
  }

  _freeGiftRemovedNoticeCopy() {
    const els = document.querySelectorAll("cart-drawer");
    for (let i = 0; i < els.length; i++) {
      const el = els[i];
      let opts = el.freeGiftController?.options || el.options;
      if (!opts) {
        const raw = el.getAttribute("data-settings");
        if (!raw) continue;
        try {
          opts = JSON.parse(raw);
        } catch (e) {
          continue;
        }
      }
      const t = opts?.freeGiftRemovedNotice;
      if (typeof t === "string" && t.trim()) return t.trim();
    }
    const mainCartNotice = this.getMainCartOptions()?.freeGiftRemovedNotice;
    if (typeof mainCartNotice === "string" && mainCartNotice.trim()) return mainCartNotice.trim();
    return this.removedNoticeFallback;
  }

  renderFreeGiftRemovedNotice() {
    document.querySelectorAll(".cart-drawer__free-gift-removed-note").forEach((n) => n.remove());

    if (!this.showRemovedNotice) return;

    const text = this._freeGiftRemovedNoticeCopy();
    const root = this.element;

    /** @type {Element[]} */
    const anchors = [];
    const drawerShipping =
      root && typeof root.querySelector === "function"
        ? root.querySelector(".cart-drawer__shipping")
        : null;
    if (drawerShipping) anchors.push(drawerShipping);
    document.querySelectorAll("main-cart .cart-drawer__shipping").forEach((el) => {
      if (!anchors.includes(el)) anchors.push(el);
    });

    if (anchors.length === 0) return;

    for (let i = 0; i < anchors.length; i++) {
      const note = document.createElement("p");
      note.className = "cart-drawer__free-gift-removed-note u-p4";
      note.textContent = text;
      anchors[i].insertAdjacentElement("afterend", note);
    }
  }

  setFreeGiftRemovedNotice(show) {
    const value = show === true;
    try {
      if (!value) {
        sessionStorage.removeItem(this.storageKey);
      }
    } catch (e) { }

    document.querySelectorAll("cart-drawer").forEach((el) => {
      if (el.freeGiftController) {
        el.freeGiftController._setShowRemovedNotice(value);
      } else {
        el._showFreeGiftRemovedNotice = value;
      }
    });

    const primary = this.getPrimaryDrawer();
    if (primary?.freeGiftController) {
      primary.freeGiftController.renderFreeGiftRemovedNotice();
    } else {
      this.renderFreeGiftRemovedNotice();
    }
  }

  _setFreeGiftSyncOverlayVisible(visible) {
    document.querySelectorAll("[data-cart-items-shell]").forEach((shell) => {
      const overlay = shell.querySelector("[data-free-gift-sync-overlay]");
      if (!overlay) return;
      if (visible) {
        shell.classList.add("cart-items-shell--sync-active");
        overlay.setAttribute("aria-hidden", "false");
        overlay.setAttribute("aria-busy", "true");
      } else {
        shell.classList.remove("cart-items-shell--sync-active");
        overlay.setAttribute("aria-hidden", "true");
        overlay.removeAttribute("aria-busy");
      }
    });
  }

  _hasFreeGiftProgram() {
    const o = this.options || {};
    return Boolean(o.freeGiftVariantId && String(o.freeGiftLimit || "").trim() !== "");
  }

  _releaseFreeGiftReplaceContentGate() {
    this.setReplaceContentPending(false);
    if (!this.syncInFlight) {
      this._setFreeGiftSyncOverlayVisible(false);
    }
  }

  _isFreeGiftSyncPending() {
    if (this.syncInFlight === true || this.syncQueued === true) {
      return true;
    }
    if (!this._hasFreeGiftProgram()) {
      return false;
    }
    return (
      this.replaceContentPending === true ||
      this.owner?._quantityUpdateController != null
    );
  }

  abort() {
    try {
      this.abortController?.abort();
    } catch (e) { }
  }

  _markCheckoutStarted() {
    if (this.owner) this.owner._checkoutInProgress = true;
    window.__checkoutInProgress = true;
    this.abort();
  }

  onDocumentEnterWhilePending(event) {
    if (!event || event.key !== "Enter") return;
    if (this.isCheckoutInProgress() || !this.isAnyGateActive()) return;
    const target = event.target;
    if (!target || typeof target.closest !== "function") return;
    const isInsideCart =
      target.closest("cart-drawer") ||
      target.closest("main-cart") ||
      target.closest('form[action="/checkout"]');
    if (!isInsideCart) return;
    event.preventDefault();
    event.stopPropagation();
  }

  onCheckoutIntentCapture(event) {
    const t = event?.target;
    if (!t || typeof t.closest !== "function") return;
    const checkoutControl = t.closest(
      [
        'button[name="checkout"]',
        'input[name="checkout"]',
        ".cart-drawer__checkout",
        ".shopify-payment-button__button",
        ".shopify-payment-button__more-options",
        "shopify-accelerated-checkout",
        "[data-testid^='Checkout-button']",
      ].join(", ")
    );
    const inCheckoutForm =
      t.closest('form[action="/checkout"]') &&
      (t.closest('button[type="submit"]') ||
        t.closest('input[type="submit"]') ||
        (t.matches && t.matches('button[type="submit"]')) ||
        (t.matches && t.matches('input[type="submit"]')));
    if (checkoutControl || inCheckoutForm) {
      const standardCheckoutControl =
        inCheckoutForm ||
        t.closest('button[name="checkout"]') ||
        t.closest('input[name="checkout"]') ||
        t.closest(".cart-drawer__checkout");
      if (!standardCheckoutControl && this.isAnyGateActive()) {
        event.preventDefault();
        event.stopPropagation();
      }
    }
  }

  async _refreshAfterFreeGiftMutation() {
    if (typeof this.owner?._refreshAfterFreeGiftMutation === "function") {
      await this.owner._refreshAfterFreeGiftMutation();
    }
  }

  async syncFreeGift({
    cartData: providedCartData = null,
    skipRefreshAfterMutation = false,
    deferOverlayUntilMutation = false,
  } = {}) {
    if (this.isCheckoutInProgress()) {
      this._releaseFreeGiftReplaceContentGate();
      return;
    }
    this.updateOptions(this.getMainCartOptions());
    if (this.syncInFlight) {
      this._setSyncQueued(true);
      return;
    }
    this._setSyncQueued(false);

    const variantId = this.options?.freeGiftVariantId;
    const limitString = this.options?.freeGiftLimit;
    const currencyIsoCode = this.options?.currencyIsoCode;

    if (!variantId || !limitString) {
      this.setFreeGiftRemovedNotice(false);
      this._releaseFreeGiftReplaceContentGate();
      return;
    }

    const giftLimitCents = this.parseCurrencyLimitToCents(limitString, currencyIsoCode);
    if (!giftLimitCents || giftLimitCents <= 0) {
      this.setFreeGiftRemovedNotice(false);
      this._releaseFreeGiftReplaceContentGate();
      return;
    }

    const propertyKey = this.options?.freeGiftPropertyKey || "_free_gift";
    const propertyValue = this.options?.freeGiftPropertyValue || "true";

    this._setSyncInFlight(true);
    const showSyncOverlay = () => {
      this._setFreeGiftSyncOverlayVisible(true);
    };
    if (!deferOverlayUntilMutation) {
      showSyncOverlay();
    }
    const ac = new AbortController();
    this._setAbortController(ac);
    const signal = ac.signal;

    try {
      const cartData =
        providedCartData ||
        (await window.ThemeCart.CartApi.fetchCart(signal));
      if (this.isCheckoutInProgress()) return;

      const items = Array.isArray(cartData?.items) ? cartData.items : [];
      if (!cartData || !Array.isArray(cartData.items)) return;

      const variantIdStr = String(variantId);
      let paidMerchCents = 0;
      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (this._lineExcludedFromPaidMerch(item, variantIdStr, propertyKey, propertyValue)) {
          continue;
        }
        paidMerchCents += this._cartLineTotalCents(item);
      }

      let freeGiftQty = 0;
      /** @type {string[]} */
      const freeGiftLineKeys = [];
      /** @type {number[]} 1-based line indexes for /cart/change.js if key missing */
      const freeGiftLineIndexes = [];
      /** @type {number[]} */
      const freeGiftLineVariantIds = [];

      for (let i = 0; i < items.length; i++) {
        const item = items[i];
        if (!this._lineQualifiesAsThemeFreeGiftSlot(item, variantIdStr, propertyKey, propertyValue))
          continue;

        const q = item.quantity || 0;
        freeGiftQty += q;
        if (item.key) {
          freeGiftLineKeys.push(String(item.key));
        } else {
          freeGiftLineIndexes.push(i + 1);
        }
        if (item.variant_id != null && item.variant_id !== "") {
          freeGiftLineVariantIds.push(Number(item.variant_id));
        }
      }

      const giftQualified = paidMerchCents >= giftLimitCents;

      if (giftQualified) {
        this.setFreeGiftRemovedNotice(false);
        if (freeGiftQty < 1) {
          if (this.isCheckoutInProgress()) return;
          showSyncOverlay();
          await window.ThemeCart.CartApi.add(
            {
              id: variantId,
              quantity: 1,
              properties: {
                [propertyKey]: propertyValue,
              },
            },
            signal
          );
          if (this.isCheckoutInProgress()) return;

          if (!skipRefreshAfterMutation) {
            await this._refreshAfterFreeGiftMutation();
          }
        }
      } else {
        const changeHeaders = {
          "Content-Type": "application/json",
          "X-Requested-With": "XMLHttpRequest",
        };
        const changeUrl = `${window.routes.localeRootUrl}/cart/change.js`;
        if (this.isCheckoutInProgress()) return;
        let swept = false;
        if (providedCartData && (freeGiftLineKeys.length > 0 || freeGiftLineIndexes.length > 0)) {
          showSyncOverlay();
          await this._removeThemeFreeGiftLines(
            changeUrl,
            changeHeaders,
            freeGiftLineKeys,
            freeGiftLineIndexes,
            variantIdStr,
            freeGiftLineVariantIds
          );
          swept = true;
        } else if (
          deferOverlayUntilMutation &&
          freeGiftLineKeys.length === 0 &&
          freeGiftLineIndexes.length === 0
        ) {
          swept = false;
        } else {
          showSyncOverlay();
          swept = await this._sweepRemoveThemeFreeGiftLines(
            changeUrl,
            changeHeaders,
            variantIdStr,
            propertyKey,
            propertyValue
          );
        }
        if (this.isCheckoutInProgress()) return;
        if (swept) {
          this.setFreeGiftRemovedNotice(true);
          if (!skipRefreshAfterMutation) {
            await this._refreshAfterFreeGiftMutation();
          }
        }
      }
    } catch (err) {
      if (err && err.name === "AbortError") return;
      if (window.DEBUG_CART_RECOS) console.warn("[cart-drawer] syncFreeGift failed", err);
    } finally {
      this._setSyncInFlight(false);
      this._setAbortController(null);
      const queued = this.syncQueued;
      if (queued) {
        this._setSyncQueued(false);
      }
      if (queued && !this.isCheckoutInProgress()) {
        if (!deferOverlayUntilMutation) {
          this._setFreeGiftSyncOverlayVisible(true);
        }
        void this.syncFreeGift({ deferOverlayUntilMutation });
      } else {
        this._releaseFreeGiftReplaceContentGate();
      }
    }
  }

  _optimisticallyRemoveIneligibleFreeGiftLine(cartData) {
    const items = Array.isArray(cartData?.items) ? cartData.items : [];
    if (items.length === 0) return;

    const variantId = this.options?.freeGiftVariantId;
    const limitString = this.options?.freeGiftLimit;
    const currencyIsoCode = this.options?.currencyIsoCode;
    if (!variantId || !limitString) return;

    const giftLimitCents = this.parseCurrencyLimitToCents(limitString, currencyIsoCode);
    if (!giftLimitCents || giftLimitCents <= 0) return;

    const variantIdStr = String(variantId);
    const propertyKey = this.options?.freeGiftPropertyKey || "_free_gift";
    const propertyValue = this.options?.freeGiftPropertyValue || "true";

    let paidMerchCents = 0;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (this._lineExcludedFromPaidMerch(item, variantIdStr, propertyKey, propertyValue)) {
        continue;
      }
      paidMerchCents += this._cartLineTotalCents(item);
    }

    if (paidMerchCents >= giftLimitCents) return;

    const giftLine = items.find((item) =>
      this._lineQualifiesAsThemeFreeGiftSlot(item, variantIdStr, propertyKey, propertyValue)
    );
    if (!giftLine) return;

    const lineDomId = window.ThemeCart.CartLineRules.itemDomIdFromLineKey(giftLine.key);
    if (!lineDomId) return;

    const lineEl = this.element?.querySelector?.(`#${lineDomId}`);
    if (!lineEl) return;

    if (typeof this.owner?.removeCartItem === "function") {
      this.owner.removeCartItem(lineEl);
    } else {
      lineEl.remove();
    }
  }
}

window.ThemeCart.FreeGiftController = ThemeCartFreeGiftController;
